/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Fri May 10 2024
 **/
import { EmotionJSX } from "@emotion/react/types/jsx-namespace";
import styled from "@emotion/styled";
import { useTheme } from "@emotion/react";

import type { IconName } from "@/frameworks/designStuff/components";
import { _T, _P } from "@rfice/language";

import { Row } from "../../Layouts";
import { Typography } from "../../Typography";
import { Icon } from "../Icon";
import Popup from "./private/Popup";

type Text = string | ReturnType<typeof _T> | ReturnType<typeof _P>;

interface ModalPopup {
	modalID: string;
	width?: 440 | 480 | 600;
	fitContent?: boolean;
	title?: Text;
	titleTypography?: DesignStuff.Typography.Props;
	titleIcon?: IconName;
	hasBackgroundIcon?: boolean;
	children?: React.ReactNode;
	cancelText?: string;
	confirmText?: string;
	stretchBottom?: boolean;
	onCancel?: (ev: React.MouseEvent) => void;
	onConfirm?: (ev: React.MouseEvent | KeyboardEvent, hide: () => void) => void;
	rightIcon?: EmotionJSX.Element;
}

export default function ModalPopup(props: ModalPopup) {
	const theme = useTheme();

	return (
		<Popup modalID={props.modalID} width={props.width} fitContent={props.fitContent}>
			{props.title && (
				<Popup.Title>
					<Row gap={8} css={{ width: "100%" }} verticalAlign="center">
						{props.titleIcon &&
							(props.hasBackgroundIcon ?? true ? (
								<IconCircle verticalAlign="center" horizonAlign="center">
									<Icon size={18} fill={theme.colors.deco6_50_100} name={props.titleIcon} />
								</IconCircle>
							) : (
								<Icon size={18} fill={theme.colors.deco6_50_100} name={props.titleIcon} />
							))}
						<Typography
							{...props.titleTypography}
							tag={typeof props.title === "string" ? "span" : "div"}
							typoColor={props.titleTypography?.typoColor ?? "neutral_80_100"}
							typoSize={props.titleTypography?.typoSize ?? "Head6"}
						>
							{props.title}
						</Typography>
						{props.rightIcon && (
							<RightIconWrapper verticalAlign="center">{props.rightIcon}</RightIconWrapper>
						)}
					</Row>
				</Popup.Title>
			)}
			{props.children && <Popup.Contents>{props.children}</Popup.Contents>}
			{(props.cancelText || props.confirmText) && <Popup.Bottom {...props} stretch={props.stretchBottom} />}
		</Popup>
	);
}

const IconCircle = styled(Row)`
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: ${({ theme }) => theme.colors.deco6_10_100};
`;

const RightIconWrapper = styled(Row)`
	margin-left: auto;
	&:hover {
		background-color: ${({ theme, disabled }) => !disabled && theme.colors.secondary_80_8};
		border-radius: 50%;
	}
`;
